import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { EditableDocument } from '@/stores/structure-proposal-store'

const TYPE_LABELS: Record<EditableDocument['documentType'], string> = {
  'multi-section': 'Multi-section',
  single: 'Single document',
  questionnaire: 'Questionnaire',
}

interface ProposedDocumentCardProps {
  document: EditableDocument
  onIncludedChange: (included: boolean) => void
  onNameChange: (name: string) => void
}

export function ProposedDocumentCard({
  document,
  onIncludedChange,
  onNameChange,
}: ProposedDocumentCardProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-sm',
        !document.included && 'opacity-55 bg-muted/40 shadow-none'
      )}
    >
      <Switch
        checked={document.included}
        onCheckedChange={onIncludedChange}
        aria-label={document.name.trim() ? `Include ${document.name}` : 'Include this document'}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        {/* The title row carries only the short, bounded type badge. The format
            note is a tender instruction of unbounded length ("Microsoft Word
            and Microsoft Project in PDF format"), and as an unshrinkable badge
            up here it crushed the name field until it read as the title — so it
            lives on the wrapping metadata line below instead (F40). */}
        <div className="flex items-center gap-3">
          <Input
            value={document.name}
            onChange={(e) => onNameChange(e.target.value)}
            disabled={!document.included}
            placeholder="Untitled document"
            // A native input hard-clips with no ellipsis, so a long name is
            // otherwise unreadable without selecting through it.
            title={document.name}
            className="h-7 min-w-0 flex-1 border-transparent bg-transparent px-1 text-sm font-medium hover:border-border focus-visible:border-border"
            aria-label="Document name"
          />
          <Badge variant="default" className="shrink-0">
            {TYPE_LABELS[document.documentType]}
          </Badge>
        </div>
        <p className="mt-1 text-meta">
          {document.formatNote && <span>{document.formatNote} · </span>}
          {document.wordBudget != null && (
            <span>Word budget {document.wordBudget.toLocaleString()} · </span>
          )}
          {document.documentType === 'questionnaire' && (
            <span>Extract questions inside the document after creating · </span>
          )}
          {document.rationale}
        </p>
      </div>
    </div>
  )
}
